import * as React from 'react'
import { Icon, IconName } from '@blueprintjs/core'
import classNames from 'classnames'

/**
 * Drop-in replacement for `HTMLSelect` from `@blueprintjs/core`.
 *
 * Matches Blueprint's API surface so callsites can switch by changing the
 * import. Visuals match the project's *rendered* Blueprint styling, NOT
 * Blueprint's stock styling — `app.scss:573` overrides `.bp4-html-select
 * select` with: white background, 8px border-radius, 1px solid
 * rgba(28,33,39,0.2) border (instead of Blueprint's inset shadow), 33px
 * fixed height, and `box-shadow: none`. We replicate that here directly
 * so the shared component looks identical in production.
 *
 * Supported props:
 *   - options          → Array<string | number | OptionProps>
 *                        (mutually exclusive with `children`, same as Blueprint)
 *   - value            → controlled value
 *   - onChange         → standard <select> onChange
 *   - disabled         → disabled state
 *   - fill             → width: 100% on the wrapper + select
 *   - large            → 40px tall instead of 30px
 *   - minimal          → no shadow/background until hover
 *   - iconName         → icon shown in the dropdown indicator
 *                        (default 'double-caret-vertical')
 *   - iconProps        → extra props for the indicator icon
 *   - className/style  → forwarded to the wrapper div
 *   - All other props (id, name, onBlur, etc.) → forwarded to the <select>
 *
 * Usage audit (2026-05-07): 22 files, 26 <HTMLSelect> tags. Real props:
 * onChange (25), value (24), options (18), className (12), fill (8),
 * iconProps (6 — all `{ icon: 'caret-down' }`), disabled (2), minimal (1),
 * id (1), style (1). All supported. No callsite uses `large` but kept for
 * API parity.
 */

export interface OptionProps {
    label?: React.ReactNode
    value: string | number
    disabled?: boolean
    className?: string
}

export interface HTMLSelectProps
    extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'> {
    options?: Array<string | number | OptionProps>
    value?: string | number
    onChange?: React.ChangeEventHandler<HTMLSelectElement>
    disabled?: boolean
    fill?: boolean
    large?: boolean
    minimal?: boolean
    iconName?: IconName
    iconProps?: Partial<React.ComponentProps<typeof Icon>>
    className?: string
    style?: React.CSSProperties
    elementRef?: React.Ref<HTMLSelectElement>
}

const HTMLSelect = React.forwardRef<HTMLDivElement, HTMLSelectProps>(function HTMLSelect(
    {
        className,
        style,
        disabled,
        fill,
        large,
        minimal,
        iconName = 'double-caret-vertical',
        iconProps,
        options,
        value,
        onChange,
        children,
        elementRef,
        ...selectProps
    },
    ref,
) {
    // Render the option list. Blueprint accepts:
    //   - string | number       → <option value=X>X</option>
    //   - { label, value, disabled, className }
    const optionChildren = options?.map((option) => {
        const props: OptionProps = typeof option === 'object' ? option : { value: option }
        return (
            <option
                key={String(props.value)}
                value={props.value}
                disabled={props.disabled}
                className={props.className}
            >
                {props.label ?? props.value}
            </option>
        )
    })

    return (
        <div
            ref={ref}
            style={style}
            className={classNames(
                'relative inline-block align-top',
                fill && 'w-full',
                // No wrapper opacity when disabled — the project's
                // Blueprint render only dims the text (via the override
                // chain), not the whole control. Cursor cue is enough on
                // the wrapper.
                disabled && 'cursor-not-allowed',
                className,
            )}
        >
            <select
                {...selectProps}
                ref={elementRef}
                value={value}
                onChange={onChange}
                disabled={disabled}
                className={classNames(
                    // Layout: matches the project's `.bp4-html-select select`
                    // override in `app.scss` — 33px fixed height, 14px font,
                    // 5px/10px padding, 8px border-radius, 1px solid border.
                    // The override applies regardless of `large`, `minimal`,
                    // or `:disabled`, so every variant shares the same
                    // chrome (white bg + gray border + 33px height). We do
                    // the same here.
                    'appearance-none w-full text-left align-top text-[14px] font-normal',
                    'rounded-lg cursor-pointer outline-none',
                    // Visible focus ring (a11y — Blueprint's stock focus
                    // ring is suppressed by the project override's
                    // `box-shadow: none !important`, so we provide our own
                    // with a Tailwind ring utility on `:focus-visible`).
                    'focus-visible:ring-2 focus-visible:ring-[#2d72d2] focus-visible:ring-offset-1',
                    'transition-colors duration-100',
                    'h-[33px] pl-[10px] pr-[25px] py-[5px]',
                    // Blueprint stock `large` would force 40px height + 16px
                    // font, but the project override pins height to 33px no
                    // matter what. Honor the prop for font-size only so
                    // callsites that pass `large` get *some* visual hint.
                    large && 'text-[16px]',
                    // Border + white bg apply on every variant. `minimal`
                    // can't drop them because the override forces them; we
                    // match that rendered behavior.
                    'bg-white text-[#1c2127] border border-[rgba(28,33,39,0.2)]',
                    !disabled && 'hover:bg-gray-50 active:bg-gray-100',
                    // Disabled keeps the border + white bg (forced by the
                    // override); only the text dims and the cursor changes.
                    disabled && 'cursor-not-allowed text-[rgba(95,107,124,0.6)]',
                )}
            >
                {optionChildren}
                {children}
            </select>

            {/* Dropdown caret icon overlay. Absolute-positioned at the right
                edge of the select, vertically centered. Blueprint renders an
                `<Icon icon="double-caret-vertical">` here; we use the same. */}
            <span
                className={classNames(
                    'pointer-events-none absolute right-[7px] top-1/2 -translate-y-1/2',
                    'text-[#5f6b7c]',
                    disabled && 'text-[rgba(95,107,124,0.6)]',
                )}
            >
                <Icon icon={iconName} title="Open dropdown" {...iconProps} />
            </span>
        </div>
    )
})

export { HTMLSelect }
